import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import style from './DefaultAppNav.module.css';

import { Lightbox } from '../../Lightbox';
import { Link } from '../../Link';
import { Logo } from '../../Logo';
import { Hamburger } from './Hamburger';
import { Menu } from './Menu';
import { Nav } from './Nav';
import { SubNav } from './SubNav';
import { AppNavHeight } from './constants';
import type { DefaultAppNavProps } from './types';
import {
  getScrollStyle,
  useCallToActionObserver,
  useScrollProgress,
} from './utils';

const MENU_ID = 'main-menu';

/**
 * Render a responsive navigation header at application level. Make sure to wrap
 * it in a `header` element.
 *
 * @remarks
 * Use {@link Layout} with `appNav` instead, which fits better our use cases.
 * @cssVariable --tfd-appnav-height The height of the navigation header
 * @cssVariable --tfd-appnav-z-index The z-index of the navigation header
 * @see https://corgi-x.tfd.engineering/components/appnav
 */
export function DefaultAppNav(props: DefaultAppNavProps) {
  const {
    callToAction: { element: ctaEl, trigger: ctaTrigger } = {},
    leftItems = [],
    rightItems = [],
    subNavItems = [],
    logoTo: logoLink = '/',
    overlayMode = 'none',
    shiftLogoUp = false,
    menuCallToAction,
    afterMenuListEl,
    disableResponsiveLayout,
    leftItemsClassName,
    rightItemsClassName,
    onNavItemClick,
    logo = <Logo variant="product" size={AppNavHeight} />,
  } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const showCallToAction = useCallToActionObserver(ctaTrigger, ctaEl);
  const scrollProgress = useScrollProgress(rootRef);

  // Toggle off the menu when the viewport changes. This prevents it from being
  // visible when the viewport is large.
  useEffect(() => {
    const handleWindowResize = () => setIsMenuOpen(false);
    if (isMenuOpen) {
      window.addEventListener('resize', handleWindowResize);
    } else {
      window.removeEventListener('resize', handleWindowResize);
    }
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isMenuOpen]);

  /** Items to place in the menu */
  const menuItems = [...subNavItems, ...leftItems, ...rightItems];

  /** If item has a menuPosition, it should be placed last */
  menuItems.sort(
    (a, b) =>
      (a.menuPosition === 'last' ? 1 : 0) - (b.menuPosition === 'last' ? 1 : 0)
  );
  /** Items to keep visible when the menu is not open. */
  const visibleSubNavItems = subNavItems.filter(item => item.alwaysVisible);

  const scrollStyle = getScrollStyle(scrollProgress, {
    mode: overlayMode !== 'none' ? overlayMode : undefined,
    overlay: overlayMode !== 'none',
    shiftLogoUp,
    isMenuOpen,
  });

  const navLogo = (
    <span
      className={style.logo}
      style={{
        transform: `translateY(${scrollStyle.logoTranslateY}px)`,
      }}
    >
      <Link to={logoLink} onClick={() => setIsMenuOpen(false)}>
        {logo}
      </Link>
    </span>
  );

  const menuButton = menuItems.length > 0 && (
    <Hamburger
      menuId={MENU_ID}
      isOpen={isMenuOpen}
      isDark={overlayMode === 'dark'}
      onClick={() => setIsMenuOpen(!isMenuOpen)}
    />
  );

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    props.onMenuClose?.();
  };
  return (
    <div
      ref={rootRef}
      className={classNames(style.root, props.className, {
        [style.isOpen]: isMenuOpen,
      })}
      style={{
        boxShadow: scrollStyle.boxShadow,
        backgroundColor: scrollStyle.backgroundColor,
        ...props.style,
      }}
      data-overlay-mode={overlayMode !== 'none' && scrollStyle.mode}
    >
      <div ref={navRef}>
        <Nav
          logo={navLogo}
          leftItems={leftItems}
          rightItems={rightItems}
          showVisibleRightItems={!isMenuOpen}
          ctaEl={showCallToAction && !isMenuOpen ? ctaEl : undefined}
          disableResponsiveLayout={disableResponsiveLayout}
          menuButton={menuButton || undefined}
          leftItemsClassName={leftItemsClassName}
          rightItemsClassName={rightItemsClassName}
          onNavItemClick={onNavItemClick}
        />
      </div>
      <Lightbox
        className={style.menuLightbox}
        offsetTop={AppNavHeight}
        isOpen={isMenuOpen}
        onOpen={props.onMenuOpen}
        onClosed={props.onMenuClose}
        onClose={handleMenuClose}
        backdropClassName={style.menuBackdrop}
        closeOnBackdropClick
        focusTraps={isMenuOpen ? [navRef] : []}
        role="presentation"
        controlVisibility
      >
        <Menu
          id={MENU_ID}
          items={menuItems}
          cta={menuCallToAction}
          afterMenuListEl={afterMenuListEl}
          onNavItemClick={onNavItemClick}
        />
      </Lightbox>
      {!isMenuOpen && visibleSubNavItems.length > 0 && (
        <SubNav items={visibleSubNavItems} />
      )}
    </div>
  );
}
